import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static identifier = "idle"
    static values = {
        maxTime: Number, // 24h in seconds
        basePerSecondGain: Number, // base gain (not multiplied)
        basePerSecondGainRc: Number,
        yieldEventMultiplier: Number,
        initialElapsed: Number, // seconds already elapsed when page loaded
        initialReward: Number, // already accrued DC
        initialRewardRc: Number, // already accrued RC
        tiers: Array, // [{ name, max, rate }, ...]
        rewardCategory: String,
        currentRcPotential: Number, // current RC potential from worker's current_stat
        rcPotentialLimit: Number, // RC potential soft cap limit
        doomMultiplier: Number // D.O.O.M boost multiplier for exit rewards
    };

    static targets = [
        "rewardDisplay", "rewardDisplayRC", "progressBar", "progressText",
        "tierName", "tierRate", "tierTime", "gainDisplay", "gainDisplayRC",
        "dcEarningDisplay", "dcEarningBoostDisplay",
        "gainDisplayYieldBonus", "dcEarningYieldBonusDisplay",
        "rewardDisplayYieldBonusRC", "gainDisplayYieldBonusRC", "rcEarningYieldBonusDisplay",
        "rcEarningFullDisplay", "rcEarningReducedDisplay", "rcEarningBoostDisplay",
        "rcLimitCurrentBar", "rcLimitProjectedBar", "rcLimitText", "rcEfficiencyText"
    ];

    connect() {
        this.currentDC = this.initialRewardValue || 0;
        this.currentRC = this.initialRewardRcValue || 0;
        this.cooldownPeriod = 10

        // Use server-provided elapsed time instead of browser time
        this.elapsedSeconds = this.initialElapsedValue || 0;
        this.lastElapsed = this.elapsedSeconds;

        this.totalTierTime = this.tiersValue.reduce((sum, t) => sum + t.max, 0);
        this.applyProgressBarColor();

        this.update();
        this.timer = setInterval(() => this.tick(), 1000);

        // Set up page visibility change handler for auto-refresh
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnect() {
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    applyProgressBarColor() {
        if (!this.hasProgressBarTarget) return;

        // Avoid overriding the launch progress styling mid-cooldown
        if (this.progressBarTarget.dataset.state === "launching") {
            return;
        }

        this.setProgressBarClass(
            this.progressBarTarget,
            this.progressBarColorClass(this.rewardCategoryValue)
        );
    }

    progressBarColorClass(category) {
        switch (category) {
            case "deficit":
                return "progress-secondary";
            case "decay":
                return "progress-warning";
            case "penalty":
                return "progress-error";
            case "normal":
            default:
                return "progress-success";
        }
    }

    progressColorClasses() {
        return [
            "progress-primary",
            "progress-secondary",
            "progress-accent",
            "progress-info",
            "progress-success",
            "progress-warning",
            "progress-error"
        ];
    }

    setProgressBarClass(bar, colorClass) {
        if (!bar) return;
        bar.classList.remove(...this.progressColorClasses());
        if (colorClass) {
            bar.classList.add(colorClass);
        }
    }

    percentColorClass(percent) {
        if (percent >= 100) return "progress-error";
        if (percent >= 80) return "progress-warning";
        return "progress-success";
    }

    numberFormatter() {
        if (!this._numberFormatter) {
            this._numberFormatter = new Intl.NumberFormat();
        }
        return this._numberFormatter;
    }

    tick() {
        // Increment elapsed time by 1 second
        this.elapsedSeconds += 1;
        this.update();
    }

    update() {
        const cappedElapsed = Math.min(this.elapsedSeconds, this.maxTimeValue);
        const elapsedSinceLastTick = this.elapsedSeconds - this.lastElapsed;
        this.lastElapsed = this.elapsedSeconds;

        // Add incremental rewards, capped at maxTime
        const newRewards = this.calculateIncrementalRewards(cappedElapsed - elapsedSinceLastTick, cappedElapsed);
        this.currentDC += newRewards.dc;
        this.currentRC += newRewards.rc;

        // Update UI
        this.updateProgress(cappedElapsed);
        this.updateTierDisplay(cappedElapsed);
        this.updateRewardDisplay();
        this.updateRcLimitDisplay();
    }

    calculateIncrementalRewards(fromSec, toSec) {
        const effectiveStart = Math.max(fromSec - this.cooldownPeriod, 0);
        const effectiveEnd = Math.max(toSec - this.cooldownPeriod, 0);

        const start = Math.min(effectiveStart, this.maxTimeValue);
        const end = Math.min(effectiveEnd, this.maxTimeValue);
        if (end <= start) return { dc: 0, rc: 0 };

        let earnedDC = 0, earnedRC = 0;
        let processed = 0;

        for (const tier of this.tiersValue) {
            const tierStart = processed;
            const tierEnd = processed + tier.max;
            processed = tierEnd;

            if (start >= tierEnd) continue; // skip finished tiers
            const startInTier = Math.max(start, tierStart);
            const endInTier = Math.min(end, tierEnd);
            const seconds = Math.max(0, endInTier - startInTier);

            earnedDC += seconds * this.basePerSecondGainValue * tier.rate;
            earnedRC += seconds * this.basePerSecondGainRcValue * tier.rate;
            if (end <= tierEnd) break;
        }

        return { dc: earnedDC, rc: earnedRC };
    }

    updateProgress(elapsed) {
        const cooldownPeriod = this.cooldownPeriod;
        const maxTime = Math.min(this.maxTimeValue, this.totalTierTime);

        if (elapsed < cooldownPeriod) {
            const cooldownPercent = (elapsed / cooldownPeriod) * 100;

            if (this.hasProgressBarTarget) {
                const bar = this.progressBarTarget;
                bar.max = 100;
                bar.value = cooldownPercent;
                bar.dataset.state = "launching";
                this.setProgressBarClass(bar, "progress-info");
            }

            if (this.hasProgressTextTarget) {
                this.progressTextTarget.textContent = `${this.formatTime(elapsed)} / ${this.formatTime(cooldownPeriod)} (launching)`;
            }
            return;
        }

        const effectiveElapsed = elapsed - cooldownPeriod;
        const percent = Math.min((effectiveElapsed / maxTime) * 100, 100);

        if (this.hasProgressBarTarget) {
            const bar = this.progressBarTarget;
            bar.max = 100;
            bar.value = percent;
            bar.dataset.state = "active";
            this.applyProgressBarColor();
        }

        if (this.hasProgressTextTarget) {
            this.progressTextTarget.textContent = `${this.formatTime(effectiveElapsed)} / ${this.formatTime(maxTime)}`;
        }
    }

    updateTierDisplay(elapsed) {
        const cooldownPeriod = this.cooldownPeriod;

        // Show launching state during cooldown
        if (elapsed < cooldownPeriod) {
            this.showLaunchingState(cooldownPeriod - elapsed);
            return;
        }

        // After cooldown, calculate effective elapsed time for tier display
        const effectiveElapsed = elapsed - cooldownPeriod;
        let remaining = effectiveElapsed;

        for (const tier of this.tiersValue) {
            if (remaining < tier.max) {
                this.showTier(tier, tier.max - remaining);
                return;
            }
            remaining -= tier.max;
        }

        // If elapsed > all tiers → lock on last tier with 0 remaining
        const lastTier = this.tiersValue[this.tiersValue.length - 1];
        this.showTier(lastTier, 0);
    }

    showLaunchingState(remainingCooldown) {
        if (this.hasTierNameTarget) this.tierNameTarget.textContent = "LAUNCHING ASYNC DIVISION";
        if (this.hasTierRateTarget) this.tierRateTarget.textContent = "Initializing Systems...";
        if (this.hasTierTimeTarget) {
            this.tierTimeTarget.textContent = `${this.formatTime(remainingCooldown)} until rewards start`;
        }
        if (this.hasGainDisplayTarget) {
            this.gainDisplayTarget.textContent = `0.0000 DC/s`;
        }
        if (this.hasGainDisplayYieldBonusTarget) {
            this.gainDisplayYieldBonusTarget.textContent = `Yield bonus: +0.0000 DC/s (0%)`;
        }
        if (this.hasGainDisplayRCTarget) {
            this.gainDisplayRCTarget.textContent = `0.0000 RC Potential/s`;
        }
        if (this.hasGainDisplayYieldBonusRCTarget) {
            this.gainDisplayYieldBonusRCTarget.textContent = `Yield bonus: +0.0000 RC Potential/s (0%)`;
        }
    }

    showTier(tier, remainingTime) {
        if (this.hasTierNameTarget) this.tierNameTarget.textContent = tier.name;
        if (this.hasTierRateTarget) this.tierRateTarget.textContent = `${(tier.rate * 100).toFixed(1)}% Efficiency`;
        if (this.hasTierTimeTarget) {
            if (remainingTime <= 0) {
                this.tierTimeTarget.textContent = `MAX TIME REACHED`;
            } else {
                this.tierTimeTarget.textContent = `${this.formatTime(remainingTime)} remaining in tier`;
            }
        }
        if (this.hasGainDisplayTarget) {
            this.gainDisplayTarget.textContent = `${(this.basePerSecondGainValue * tier.rate).toFixed(4)} DC/s`;
        }
        if (this.hasGainDisplayYieldBonusTarget) {
            const yieldMult = (this.yieldEventMultiplierValue > 0) ? this.yieldEventMultiplierValue : 1;
            const bonus = (this.basePerSecondGainValue * tier.rate) * (yieldMult - 1);
            const percent = (yieldMult - 1) * 100;
            this.gainDisplayYieldBonusTarget.textContent = `Yield bonus: +${bonus.toFixed(4)} DC/s (${percent.toFixed(0)}%)`;
        }
        if (this.hasGainDisplayRCTarget) {
            this.gainDisplayRCTarget.textContent = `${(this.basePerSecondGainRcValue * tier.rate).toFixed(4)} RC Potential/s`;
        }
        if (this.hasGainDisplayYieldBonusRCTarget) {
            const yieldMult = (this.yieldEventMultiplierValue > 0) ? this.yieldEventMultiplierValue : 1;
            const bonus = (this.basePerSecondGainRcValue * tier.rate) * (yieldMult - 1);
            const percent = (yieldMult - 1) * 100;
            this.gainDisplayYieldBonusRCTarget.textContent = `Yield bonus: +${bonus.toFixed(4)} RC Potential/s (${percent.toFixed(0)}%)`;
        }
    }

    updateRewardDisplay() {
        if (this.hasRewardDisplayTarget) this.rewardDisplayTarget.textContent = `${this.currentDC.toFixed(2)} DC`;
        if (this.hasRewardDisplayRCTarget) this.rewardDisplayRCTarget.textContent = `${this.currentRC.toFixed(2)} RC potential`;

        // Update the earning displays
        if (this.hasDcEarningDisplayTarget) {
            this.dcEarningDisplayTarget.textContent = `+${this.currentDC.toFixed(2)} DC`;
        }

        const yieldMult = (this.yieldEventMultiplierValue > 0) ? this.yieldEventMultiplierValue : 1;
        if (this.hasDcEarningYieldBonusDisplayTarget) {
            const bonus = this.currentDC * (yieldMult - 1);
            this.dcEarningYieldBonusDisplayTarget.textContent = `+${bonus.toFixed(2)} DC`;
        }
        if (this.hasRewardDisplayYieldBonusRCTarget) {
            const bonus = this.currentRC * (yieldMult - 1);
            const percent = (yieldMult - 1) * 100;
            this.rewardDisplayYieldBonusRCTarget.textContent = `Yield bonus: +${bonus.toFixed(2)} RC potential (${percent.toFixed(0)}%)`;
        }

        // Always update split RC displays
        const currentRcPotential = this.currentRcPotentialValue;
        const idleRcPotentialRaw = this.currentRC * 100;
        const limit = this.rcPotentialLimitValue;
        const spaceRemaining = Math.max(0, limit - currentRcPotential);

        const rcBreakdown = (rawPotential) => {
            if (rawPotential <= spaceRemaining) {
                return {
                    full: (rawPotential / 100.0 * 0.01),
                    reduced: 0
                };
            }

            const fullEfficiencyPortion = spaceRemaining;
            const reducedEfficiencyPortion = rawPotential - spaceRemaining;
            return {
                full: (fullEfficiencyPortion / 100.0 * 0.01),
                reduced: (reducedEfficiencyPortion / 100.0 * 0.01 * 0.5)
            };
        };

        const baseBreakdown = rcBreakdown(idleRcPotentialRaw);
        const yieldBreakdown = rcBreakdown(idleRcPotentialRaw * yieldMult);
        const doomMultiplier = (this.doomMultiplierValue > 1) ? this.doomMultiplierValue : 1;
        const boostedBreakdown = rcBreakdown(idleRcPotentialRaw * doomMultiplier * yieldMult);

        if (this.hasRcEarningFullDisplayTarget) {
            this.rcEarningFullDisplayTarget.textContent = `+${baseBreakdown.full.toFixed(2)}`;
        }
        if (this.hasRcEarningReducedDisplayTarget) {
            this.rcEarningReducedDisplayTarget.textContent = `+${baseBreakdown.reduced.toFixed(2)}`;
        }
        if (this.hasRcEarningYieldBonusDisplayTarget) {
            const baseTotal = baseBreakdown.full + baseBreakdown.reduced;
            const yieldTotal = yieldBreakdown.full + yieldBreakdown.reduced;
            const bonus = yieldTotal - baseTotal;
            this.rcEarningYieldBonusDisplayTarget.textContent = `+${bonus.toFixed(2)}`;
        }

        if (this.hasDcEarningBoostDisplayTarget) {
            const boostedDC = this.currentDC * doomMultiplier;
            const boostedWithYield = boostedDC * yieldMult;
            this.dcEarningBoostDisplayTarget.textContent = `+${boostedWithYield.toFixed(2)} DC`;
        }
        if (this.hasRcEarningBoostDisplayTarget) {
            const boostedTotal = boostedBreakdown.full + boostedBreakdown.reduced;
            this.rcEarningBoostDisplayTarget.textContent = `+${boostedTotal.toFixed(2)}`;
        }
    }

    // RC calculation using linear formula with soft cap: rc = 0.01 * rc_potential
    calculateRcEarned(rcPotentialGained) {
        const gained = Math.max(0, rcPotentialGained);
        const limit = this.rcPotentialLimitValue;
        
        if (gained <= limit) {
            // Full efficiency within limit
            return Math.round((gained / 100.0 * 0.01) * 100) / 100;
        } else {
            // Calculate base amount + reduced excess (50% efficiency)
            const baseAmount = (limit / 100.0 * 0.01);
            const excessAmount = ((gained - limit) / 100.0 * 0.01 * 0.5);
            return Math.round((baseAmount + excessAmount) * 100) / 100;
        }
    }

    formatTime(seconds) {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }

    updateRcLimitDisplay() {
        const currentRcPotential = this.currentRcPotentialValue;
        const idleRcPotentialRaw = this.currentRC * 100;
        const totalRcPotential = currentRcPotential + idleRcPotentialRaw;
        const limit = this.rcPotentialLimitValue;
        const safeLimit = Math.max(limit, 1);

        const currentPercent = Math.min((currentRcPotential / safeLimit) * 100, 100);
        const projectedPercent = Math.min((totalRcPotential / safeLimit) * 100, 100);

        if (this.hasRcLimitCurrentBarTarget) {
            const currentBar = this.rcLimitCurrentBarTarget;
            currentBar.max = 100;
            currentBar.value = currentPercent;
            currentBar.setAttribute("aria-valuenow", currentPercent.toFixed(1));
            this.setProgressBarClass(currentBar, this.percentColorClass(currentPercent));
        }

        if (this.hasRcLimitProjectedBarTarget) {
            const projectedBar = this.rcLimitProjectedBarTarget;
            projectedBar.max = 100;
            projectedBar.value = projectedPercent;
            projectedBar.setAttribute("aria-valuenow", projectedPercent.toFixed(1));
            this.setProgressBarClass(projectedBar, this.percentColorClass(projectedPercent));
        }

        if (this.hasRcLimitTextTarget) {
            const formatter = this.numberFormatter();
            const current = Math.floor(currentRcPotential / 100);
            const projected = Math.floor(totalRcPotential / 100);
            const limitFormatted = Math.floor(limit / 100);

            if (projected > current) {
                this.rcLimitTextTarget.textContent = `${formatter.format(current)} → ${formatter.format(projected)} / ${formatter.format(limitFormatted)} RC potential`;
            } else {
                this.rcLimitTextTarget.textContent = `${formatter.format(current)} / ${formatter.format(limitFormatted)} RC potential`;
            }
        }

        if (this.hasRcEfficiencyTextTarget) {
            const efficiencyLabel = this.rcEfficiencyTextTarget;
            efficiencyLabel.classList.remove("badge-success", "badge-warning", "badge-error", "badge-neutral");

            if (currentRcPotential >= limit) {
                efficiencyLabel.textContent = "50% Efficiency (Over Limit)";
                efficiencyLabel.classList.add("badge-error");
            } else if (totalRcPotential >= limit) {
                const overLimitPortion = totalRcPotential - limit;
                const underLimitPortion = limit - currentRcPotential;
                const avgEfficiency = ((underLimitPortion * 1.0 + overLimitPortion * 0.5) / (underLimitPortion + overLimitPortion)) * 100;
                efficiencyLabel.textContent = `${avgEfficiency.toFixed(0)}% Avg Efficiency`;
                efficiencyLabel.classList.add("badge-warning");
            } else {
                efficiencyLabel.textContent = "100% Efficiency";
                efficiencyLabel.classList.add("badge-success");
            }
        }
    }

    handleVisibilityChange() {
        // Only refresh when page becomes visible (user returns to tab)
        if (document.visibilityState === 'visible') {
            window.location.reload();
        }
    }
}
